import { mediaRef } from '@/services/mediaAssets';
import { galleryBySlug } from '../../../adventureGalleries';
import { defineReckonEvent } from '../../schema';

const coastalWalkImage = mediaRef('adventures/sydney-bondi-coogee-coastline.jpg');
const barTorinoImage = mediaRef('adventures/sydney-bar-torino.jpg');
const blueMountainsImage = mediaRef('adventures/sydney-blue-mountains.jpg');
const manlyBeachImage = mediaRef('adventures/sydney-manly-beach-crew-selfie.jpg');
const marrickvilleHotelImage = mediaRef('adventures/sydney-marrickville-hotel.jpg');
const bondiTrattoriaImage = mediaRef('adventures/sydney-bondi-trattoria.jpg');
const quayDinnerImage = mediaRef('adventures/sydney-quay-team-dinner.jpg');
const quayViewImage = mediaRef('adventures/sydney-quay-opera-house-view.jpg');
const rooftopPubImage = mediaRef('adventures/sydney-pubs-opera-house-rooftop.jpg');
const scenicWorldRailwayImage = mediaRef('adventures/sydney-scenic-world-railway.jpg');
const heritageHotelImage = mediaRef('adventures/sydney-australian-heritage-hotel.jpg');
const chapelDinnerImage = mediaRef('adventures/sydney-20-chapel-team-dinner.jpg');
const fourPinesImage = mediaRef('adventures/sydney-4-pines-team-lunch.jpg');
const featherdaleImage = mediaRef('adventures/sydney-featherdale.jpg');
const mountainCultureImage = mediaRef('adventures/sydney-mountain-culture-beer.jpg');
const margaretDinnerImage = mediaRef('adventures/sydney-margaret-team-dinner.jpg');
const pubTableImage = mediaRef('adventures/sydney-pubs-outdoor-table.jpg');
const teddySwimsKeynoteImage = mediaRef('adventures/sydney-teddy-swims-keynote.jpg');
const wSydneyHotelImage = mediaRef('adventures/sydney-w-hotel.jpg');

const legacy = galleryBySlug['sxsw-sydney-2025'];

const reference = (entityId, order, dayLabel, eventDescription, extra = {}) => ({
  entityId,
  order,
  dayLabel,
  eventDescription,
  ...extra,
});

const cardImage = (id, src, alt, position = 'center center', orientation = 'landscape') => ({
  id,
  src,
  alt,
  position,
  orientation,
});

const sxswSydney2025 = defineReckonEvent({
  id: 'sxsw-sydney-2025',
  slug: 'sxsw-sydney-2025',
  title: 'SXSW Sydney 2025',
  subtitle: 'SXSW brought us to Sydney. The city made the trip unforgettable.',
  eventType: 'conference',
  status: 'published',
  startDate: '2025-10-13',
  endDate: '2025-10-19',
  year: 2025,
  destination: 'Sydney',
  city: 'Sydney',
  region: 'New South Wales',
  country: 'Australia',
  heroImage: {
    id: 'sxsw-sydney-hero',
    src: legacy.heroImage,
    alt: 'Reckon heroes overlooking Sydney Harbour at sunset',
    orientation: 'landscape',
  },
  summary: legacy.summary,
  atAGlance: [
    {
      id: 'destination', label: 'Destination', value: 'Sydney, Australia', icon: 'mdi-map-marker-outline',
    },
    {
      id: 'dates', label: 'Dates', value: 'October 13–19, 2025', icon: 'mdi-calendar-range',
    },
    {
      id: 'crew', label: 'Crew', value: 6, suffix: ' travelers', icon: 'mdi-account-group-outline',
    },
    {
      id: 'home-base', label: 'Home Base', value: 'W Sydney', icon: 'mdi-bed-outline',
    },
  ],
  tripByNumbers: [
    { id: 'time-zones', label: 'Time zones crossed', value: 15 },
    { id: 'team-dinners', label: 'Team dinners', value: 4 },
    { id: 'dim-sum-firsts', label: 'First-time dim sum experiences', value: 5 },
    { id: 'concerts', label: 'Live concerts', value: 'Daily' },
    { id: 'pubs', label: 'Pubs visited', value: 'Lost count' },
    { id: 'photos', label: 'Photos taken', value: 'Thousands' },
  ],
  experienceDNA: [
    {
      id: 'food', label: 'Food', score: 100, icon: 'mdi-silverware-fork-knife',
    },
    {
      id: 'adventure', label: 'Adventure', score: 60, icon: 'mdi-compass-outline',
    },
    {
      id: 'family', label: 'Family', score: 0, icon: 'mdi-human-male-female-child',
    },
    {
      id: 'nightlife', label: 'Nightlife', score: 70, icon: 'mdi-music-note',
    },
    {
      id: 'nature', label: 'Nature', score: 60, icon: 'mdi-pine-tree',
    },
    {
      id: 'culture', label: 'Culture', score: 80, icon: 'mdi-theater',
    },
    {
      id: 'relaxation', label: 'Relaxation', score: 40, icon: 'mdi-weather-sunset',
    },
    {
      id: 'luxury', label: 'Luxury', score: 60, icon: 'mdi-star-outline',
    },
    {
      id: 'conference', label: 'Conference', score: 100, icon: 'mdi-presentation',
    },
  ],
  conference: {
    conferenceName: 'SXSW Sydney',
    venueNames: ['Darling Harbour', 'Tumbalong Park', 'Sydney conference venues'],
    conferenceDates: 'October 13–19, 2025',
    purpose: 'Learn from creators and technologists, explore the expo, make new connections, and experience how Sydney carried the conference into the city around it.',
    attendanceReason: 'SXSW brought together technology, entrepreneurship, music, and culture. We spent the formal conference hours in sessions and the rest of the week learning from Sydney itself.',
    memorableSessions: ['Teddy Swims keynote', 'Mo Gawdat keynote', 'Niall Firth on AI in 2026', 'Opening sessions', 'Expo Hall', 'Live concerts at Tumbalong Park'],
  },
  sectionContent: {
    venues: {
      title: 'The Conference Across Sydney',
      description: 'Darling Harbour and Tumbalong Park made the conference feel connected to the city around it.',
    },
    dining: {
      description: 'Coffee, team dinners, historic pubs, breweries, and neighborhood discoveries kept the story moving between sessions.',
    },
  },
  journeyStrip: [
    { id: 'day-1', label: 'Day 1', value: 'Arrival, Coogee to Bondi, and the first team dinner' },
    { id: 'day-2', label: 'Day 2', value: 'SXSW, Quay, The Rocks, and historic pubs' },
    { id: 'day-3', label: 'Day 3', value: 'Conference, dim sum, Marrickville, and 20 Chapel' },
    { id: 'day-4', label: 'Day 4', value: 'Sessions, Manly ferry, beach, and local beer' },
    { id: 'day-5', label: 'Day 5', value: 'Cocktails at Bar Torino and a final team dinner at Margaret' },
    { id: 'day-6', label: 'Day 6', value: 'Featherdale, the Blue Mountains, Scenic World, and Mountain Culture' },
  ],
  sessions: [
    reference('teddy-swims-keynote', 20, 'Conference week', 'One of the sessions that stayed with the crew beyond the conference itself.', {
      cardImage: cardImage('teddy-swims-keynote-card', teddySwimsKeynoteImage, 'Teddy Swims speaking onstage during his SXSW Sydney keynote', 'center center', 'portrait'),
      whatWeRemember: 'The keynote brought the creative side of SXSW into focus and became one of the week’s shared reference points.',
      featured: true,
    }),
    reference('mo-gawdat-human-advantage-keynote', 21, 'Conference week', 'Mo Gawdat explored why our humanity becomes more valuable, not less, as AI grows more capable.', {
      whatWeRemember: 'The session reframed AI as a reason to invest more deeply in the qualities that make us human.',
      featured: true,
    }),
    reference('niall-firth-ai-in-2026', 22, 'Conference week', 'Niall Firth looked ahead to the technologies, shifts, and questions likely to define AI in 2026.', {
      whatWeRemember: 'The session gave the crew a practical horizon for thinking about where AI is headed next.',
    }),
  ],
  venues: [
    reference('sxsw-sydney', 10, 'Days 2–4', 'Opening sessions, the expo hall, networking events, team coffee breaks, and new ideas filled the middle of each day.'),
    reference('tumbalong-park', 11, 'Every afternoon', 'Live concerts turned the end of each conference day into a shared outdoor experience.', {
      whatWeRemember: 'The music made the boundary between conference programming and the city disappear.',
      featured: true,
    }),
  ],
  places: [
    reference('sydney-harbour', 12, 'Day 2', 'The Opera House, Harbour Bridge, ferries, and evening light gave the trip its unmistakable Sydney backdrop.', {
      cardImage: cardImage('sydney-harbour-card', quayViewImage, 'The Sydney Opera House viewed across the harbor from Quay'),
    }),
    reference('the-rocks', 13, 'Day 2', 'After dinner at Quay, we wandered through one of Sydney’s oldest neighborhoods with no agenda beyond seeing what we found.', {
      cardImage: cardImage('the-rocks-card', rooftopPubImage, 'The Reckon crew at a rooftop pub overlooking Sydney Harbour'),
    }),
  ],
  activities: [
    reference('coogee-bondi-walk', 1, 'Day 1', 'We started in Coogee and followed the coastline north toward Bondi, with quiet cliffs, golden beaches, and the classic Bondi finish.', {
      cardImage: cardImage('coogee-bondi-card', coastalWalkImage, 'Rocky coastline along the Coogee to Bondi coastal walk'),
      whyWeWent: 'To shake off the long flight and begin experiencing Sydney before the conference started.',
      whatHappened: 'The walk coincided with Sculpture by the Sea, turning the coastal trail into a free outdoor gallery.',
      whatWeRemember: 'Every bend revealed another artwork against the Pacific Ocean.',
      relatedBadgeIds: ['bondi-to-coogee-coastal-walk'],
      featured: true,
    }),
    reference('manly-ferry', 40, 'Day 4', 'A ferry ride across Sydney Harbour led to fresh ocean air, Manly Beach, fish and chips, and local craft beer.', {
      cardImage: cardImage('manly-ferry-card', manlyBeachImage, 'The Reckon crew together at Manly Beach'),
      whyWeWent: 'To spend an afternoon away from the conference while experiencing one of Sydney’s essential journeys.',
      whatWeRemember: 'The skyline views made the transportation itself part of the adventure.',
      relatedBadgeIds: ['ferry-to-manly-beach'],
      featured: true,
    }),
    reference('featherdale-sydney-wildlife-park', 60, 'Day 6', 'A morning with Australia’s wildlife before continuing west toward the Blue Mountains.', {
      cardImage: cardImage('featherdale-card', featherdaleImage, 'The Reckon crew gathered around a koala at Featherdale Sydney Wildlife Park'),
      whyWeWent: 'To pet a koala, feed kangaroos, and see more of Australia’s native animals up close.',
    }),
    reference('blue-mountains', 61, 'Day 6', 'A full day outside the city revealed rainforest, the Three Sisters, and Jamison Valley.', {
      cardImage: cardImage('blue-mountains-card', blueMountainsImage, 'The Reckon crew overlooking the Three Sisters in the Blue Mountains', 'center center', 'portrait'),
      whyWeWent: 'To experience another side of New South Wales beyond the conference and harbor.',
      whatWeRemember: 'The scale of the landscape reset the pace of the week before the trip home.',
      relatedBadgeIds: ['scenic-world-blue-mountains'],
    }),
    reference('scenic-world', 62, 'Day 6', 'We rode the Scenic Railway, crossed the valley on the Skyway, and explored rainforest boardwalks below.', {
      cardImage: cardImage('scenic-world-card', scenicWorldRailwayImage, 'The Reckon crew riding the Scenic Railway at Scenic World', 'center center', 'portrait'),
    }),
  ],
  restaurants: [
    reference('bondi-trattoria', 2, 'Day 1', 'Our first team meal came after the coastal walk, just steps from Bondi Beach.', {
      cardImage: cardImage('bondi-trattoria-card', bondiTrattoriaImage, 'The Reckon crew gathered for dinner at Bondi Trattoria'),
      whatWeRemember: 'It was the perfect place to settle in, soak up the atmosphere, and officially begin the week.',
    }),
    reference('quay-sydney', 20, 'Day 1', 'Exceptional food and service met one of the world’s great dining views over the Opera House and Harbour Bridge.', {
      cardImage: cardImage('quay-card', quayDinnerImage, 'The Reckon crew dining at Quay with the Sydney Opera House behind them'),
      whatHappened: 'Amazing views of Sydney Harbour were matched by equally impressive food presentation.',
      whatWeRemember: 'An amazing way to start the week together, 14 hours ahead of home.',
      featured: true,
    }),
    reference('fortune-of-war', 21, 'Day 1', 'Fortune of War sat directly on our walk back to the hotel from Quay.', {
      whatHappened: 'We heard live music spilling out onto the street and learned it claimed to be Sydney’s “oldest” pub. How could we not stop?',
      whatWeRemember: 'An unplanned stop became the perfect continuation of the evening after Quay.',
    }),
    reference('glenmore-hotel', 22, 'Day 2', 'A rooftop pub stop with Sydney Harbour spread out below.', {
      cardImage: cardImage('glenmore-card', rooftopPubImage, 'The Reckon crew at the Glenmore rooftop with the Sydney Opera House behind them'),
    }),
    reference('australian-heritage-hotel', 23, 'Day 2', 'The Coat of Arms Pizza gave everyone a reason to try kangaroo and emu.', {
      cardImage: cardImage('heritage-hotel-card', heritageHotelImage, 'The Reckon crew sharing Coat of Arms Pizza at the Australian Heritage Hotel'),
      whatWeRemember: 'Reviews were mixed, but nobody regretted giving it a try.',
    }),
    reference('nine-dragons', 30, 'Day 3', 'For most of the crew, lunch was their first dim sum experience.', {
      whatHappened: 'Kevin ordered the classics he remembered from family trips to Philadelphia’s Chinatown and watched the team discover them for the first time.',
      whatWeRemember: 'Familiar dishes became a new shared first.',
    }),
    reference('marrickville-hotel', 31, 'Day 3', 'A casual neighborhood drink before setting off on foot toward dinner.', {
      cardImage: cardImage('marrickville-hotel-card', marrickvilleHotelImage, 'The Reckon crew sharing a drink at Marrickville Hotel'),
    }),
    reference('20-chapel', 32, 'Day 3', 'The route grew quiet enough that the crew questioned the GPS, but the hidden restaurant at the end became the unanimous favorite.', {
      cardImage: cardImage('20-chapel-card', chapelDinnerImage, 'The Reckon crew gathered for dinner at 20 Chapel'),
      whatWeRemember: 'Sometimes the best meals are the ones you have to trust the journey to find.',
      recommendation: 'The group favorite of the entire trip.',
      featured: true,
    }),
    reference('4-pines-manly', 41, 'Day 4', 'Lunch and a local craft beer at the original brew pub after crossing the harbor to Manly.', {
      cardImage: cardImage('4-pines-card', fourPinesImage, 'The Reckon crew having lunch at 4 Pines Brew Pub in Manly'),
    }),
    reference('mountain-culture', 60, 'Day 6', 'A quick stop on the drive back from the Blue Mountains became one of the highlights of the day.', {
      cardImage: cardImage('mountain-culture-card', mountainCultureImage, 'A local beer at Mountain Culture Beer Co. in the Blue Mountains'),
      whatWeRemember: 'Great beer, a welcoming room, and the wish that we had more time.',
      recommendation: 'One of our first stops if we return to the Blue Mountains.',
    }),
    reference('bar-torino', 53, 'Day 5', 'Cocktails and a chance to reset before the final team dinner.', {
      cardImage: cardImage('bar-torino-card', barTorinoImage, 'The Reckon crew gathered for cocktails at Bar Torino'),
    }),
    reference('margaret-sydney', 54, 'Day 5', 'The opportunity to experience one of Sydney’s finest steakhouses together.', {
      cardImage: cardImage('margaret-card', margaretDinnerImage, 'The Reckon crew seated together for dinner at Margaret'),
      whatWeRemember: 'Exceptional food, outstanding hospitality, and a memorable final dinner.',
      featured: true,
    }),
    reference('sydney-pub-crawl', 70, 'All week', 'Sydney’s pub scene became an adventure of its own, with a new stop, or two or three, most nights.', {
      cardImage: cardImage('sydney-pub-crawl-card', pubTableImage, 'The Reckon crew gathered around an outdoor pub table during their Sydney pub crawl'),
      whatWeRemember: 'Historic pubs, rooftop bars, harbor-side pints, and a final tally that nobody could quite remember.',
    }),
  ],
  hotels: [
    reference('w-sydney', 1, 'All week', 'W Sydney gave the crew a Darling Harbour home base within reach of the conference and the city.', {
      cardImage: cardImage('w-sydney-hotel-card', wSydneyHotelImage, 'The W Sydney hotel beside Darling Harbour', 'center center', 'portrait'),
      whyWeWent: 'Its location connected the formal conference schedule with ferry rides, dinners, walks, and nights around the harbor.',
      whatWeRemember: 'Many of the best days ended with a long walk back together.',
      featured: true,
    }),
  ],
  crew: legacy.attendees,
  meritBadges: legacy.achievements,
  gallery: legacy.media.map((item, index) => ({
    id: `sxsw-sydney-media-${index + 1}`,
    type: item.type || 'image',
    src: item.src,
    alt: item.alt,
    caption: item.caption,
    capturedAt: item.capturedAt,
    orientation: item.aspectRatio && item.aspectRatio < 1 ? 'portrait' : 'landscape',
  })),
  tags: legacy.tags,
  closingReflection: 'SXSW Sydney gave us a reason to visit one of the world’s great cities. The conference filled our days, but Sydney filled everything else, from the harbor and coastal walks to award-winning dinners, ferries, live music, hidden gems, and the moments between the itinerary. We experienced the city the way we hope to experience every destination: as a team. That’s the Reckon Way.',
});

const imageBearingSections = ['sessions', 'venues', 'places', 'restaurants', 'hotels', 'activities'];
const pageImages = [
  { image: sxswSydney2025.heroImage, caption: sxswSydney2025.subtitle },
  ...imageBearingSections.flatMap((section) => sxswSydney2025[section].map((item) => ({
    image: item.cardImage,
    caption: item.eventDescription,
  }))),
];
const galleryBySource = new Map(sxswSydney2025.gallery.map((image) => [image.src, image]));

pageImages.forEach(({ image, caption }) => {
  if (!image || galleryBySource.has(image.src)) return;
  galleryBySource.set(image.src, { ...image, caption: image.caption || caption });
});

sxswSydney2025.gallery = [...galleryBySource.values()];

export default sxswSydney2025;
